import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FaShieldVirus, FaBus, FaMapMarkedAlt, FaCheckCircle } from "react-icons/fa";

const OurExpertise = () => {
  const { t } = useTranslation();
  const trackRef = useRef(null);
  const [activeDot, setActiveDot] = useState(0);

  const expertiseData = [
    {
      id: 1,
      title: t('expertise_card1_title'),
      subtitle: t('expertise_card1_badge'),
      desc: t('expertise_card1_desc'),
      icon: <FaMapMarkedAlt />,
      color: "from-orange-main to-orange-light",
    },
    {
      id: 2,
      title: t('expertise_card2_title'),
      subtitle: t('expertise_card2_badge'),
      desc: t('expertise_card2_desc'),
      icon: <FaBus />,
      color: "from-orange-main/90 to-orange-main/70",
    },
    {
      id: 3,
      title: t('expertise_card3_title'),
      subtitle: t('expertise_card3_badge'),
      desc: t('expertise_card3_desc'),
      icon: <FaShieldVirus />,
      color: "from-orange-main to-orange-light",
    },
  ];

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const index = Math.round(track.scrollLeft / track.clientWidth);
    setActiveDot(index % expertiseData.length);
  };

  return (
    <section className="pt-12 pb-8 lg:pt-20 lg:pb-10 bg-[#fffcf9]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        
        {/* HEADER */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-3 mb-4"
          >
            <div className="h-[2px] w-12 bg-orange-main" />
            <span className="text-xs font-bold uppercase tracking-[0.4em] text-orange-main">
              {t('expertise_label')}
            </span>
            <div className="h-[2px] w-12 bg-orange-main" />
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-3xl lg:text-4xl font-bold text-gray-dark tracking-tighter"
          >
            {t('expertise_heading')}
          </motion.h2>
        </div>

        {/* EXPERTISE CARDS GRID */}
        <div 
          ref={trackRef}
          onScroll={handleScroll}
          className="flex md:grid md:grid-cols-3 gap-6 md:gap-10 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-4 md:pb-0"
        >
          {expertiseData.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="min-w-[85vw] sm:min-w-[320px] md:min-w-0 snap-start md:snap-align-none group relative bg-white rounded-[2rem] md:rounded-[3rem] p-8 md:p-10 shadow-[0_20px_50px_rgba(0,0,0,0.05)] hover:shadow-[0_40px_80px_rgba(0,0,0,0.12)] transition-all duration-500 border border-gray-50 overflow-hidden"
            >
              {/* BACKGROUND ACCENT */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gray-50 rounded-bl-full -mr-10 -mt-10 group-hover:bg-orange-main/5 transition-colors duration-500" />
              
              <div className={`w-16 h-16 md:w-20 md:h-20 rounded-2xl md:rounded-3xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white text-2xl md:text-3xl shadow-lg mb-6 md:mb-8 group-hover:scale-110 transition-transform duration-500 shadow-orange-main/20`}>
                {item.icon}
              </div>

              {/* CONTENT */}
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-3">
                  <FaCheckCircle className="text-teal-main text-xs" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-teal-main">
                    {item.subtitle}
                  </span>
                </div>
                
                <h3 className="text-xl md:text-2xl font-black text-gray-dark mb-3 md:mb-5 tracking-tight group-hover:text-orange-main transition-colors">
                  {item.title}
                </h3>
                
                <p className="text-gray-500 text-sm md:text-base leading-relaxed font-medium">
                  {item.desc}
                </p>
              </div>

              {/* DECORATIVE NUMBER (Increased Opacity) */}
              <span className="absolute bottom-6 right-10 text-8xl font-black text-gray-100 group-hover:text-orange-main/10 transition-colors pointer-events-none">
                0{index + 1}
              </span>
            </motion.div>
          ))}
        </div>

        {/* PAGINATION DOTS (MOBILE ONLY) */}
        <div className="flex justify-center gap-2 mt-4 md:hidden">
          {expertiseData.map((_, i) => (
            <span
              key={i}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                activeDot === i ? "bg-orange-main scale-110" : "bg-orange-main/30"
              }`}
            />
          ))}
        </div>

      </div>
    </section>
  );
};

export default OurExpertise;
